/**
 * ConsentManager Component
 *
 * Displays current consent status and allows users to manage their consent.
 * Supports providing and withdrawing consent per MHMDA requirements.
 * Used in the Data & Privacy settings page.
 */

import { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import {
  ConsentRecord,
  GetConsentsResponse,
  RecordConsentRequest,
  RecordConsentResponse,
  CURRENT_CONSENT_VERSION,
  PLATFORM_CONSENT_TEXT,
  formatTimestampToDate,
} from '../types/settings';
import { Check, AlertTriangle, Clock, ChevronDown, ChevronUp, Loader2, X } from 'lucide-react';

interface ConsentManagerProps {
  className?: string;
}

export function ConsentManager({ className = '' }: ConsentManagerProps): JSX.Element {
  const { signOut } = useAuth();
  const [consents, setConsents] = useState<ConsentRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [showWithdrawDialog, setShowWithdrawDialog] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    const fetchConsents = async () => {
      try {
        const getConsents = httpsCallable<Record<string, never>, GetConsentsResponse>(
          functions,
          'getConsents'
        );
        const result = await getConsents({});

        if (result.data.success) {
          setConsents(result.data.consents);
        } else {
          setError(result.data.error || 'Failed to load consent history');
        }
      } catch (err) {
        console.error('Error fetching consents:', err);
        setError('Failed to load consent history');
      } finally {
        setLoading(false);
      }
    };

    fetchConsents();
  }, []);

  // Find current platform consent
  const currentConsent = consents.find(
    (c) => c.consentType === 'platform_consent' && c.granted && c.version === CURRENT_CONSENT_VERSION
  );

  // Check if user has any valid platform consent (even if older version)
  const hasAnyPlatformConsent = consents.some(
    (c) => c.consentType === 'platform_consent' && c.granted
  );

  const handleProvideConsent = async () => {
    setActionLoading(true);
    setActionError(null);
    try {
      const recordConsent = httpsCallable<RecordConsentRequest, RecordConsentResponse>(
        functions,
        'recordConsent'
      );
      const result = await recordConsent({
        consentType: 'platform_consent',
        granted: true,
        version: CURRENT_CONSENT_VERSION,
        consentText: PLATFORM_CONSENT_TEXT,
      });

      if (result.data.success) {
        // Refresh consents list
        const getConsents = httpsCallable<Record<string, never>, GetConsentsResponse>(
          functions,
          'getConsents'
        );
        const refreshResult = await getConsents({});
        if (refreshResult.data.success) {
          setConsents(refreshResult.data.consents);
        }
      } else {
        setActionError(result.data.error || 'Failed to record consent');
      }
    } catch (err) {
      console.error('Error recording consent:', err);
      setActionError('Failed to record consent. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleWithdrawConsent = async () => {
    setActionLoading(true);
    setActionError(null);
    try {
      const recordConsent = httpsCallable<RecordConsentRequest, RecordConsentResponse>(
        functions,
        'recordConsent'
      );
      const result = await recordConsent({
        consentType: 'platform_consent',
        granted: false,
        version: CURRENT_CONSENT_VERSION,
        consentText: PLATFORM_CONSENT_TEXT,
      });

      if (result.data.success) {
        // Close dialog and sign out user
        setShowWithdrawDialog(false);
        await signOut();
      } else {
        setActionError(result.data.error || 'Failed to withdraw consent');
        setShowWithdrawDialog(false);
      }
    } catch (err) {
      console.error('Error withdrawing consent:', err);
      setActionError('Failed to withdraw consent. Please try again.');
      setShowWithdrawDialog(false);
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (timestamp?: { _seconds: number } | Date): string => {
    const date = formatTimestampToDate(timestamp);
    if (!date) return 'Unknown';
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className={`flex items-center justify-center p-8 ${className}`}>
        <Loader2 className="w-6 h-6 animate-spin text-muted" />
      </div>
    );
  }

  if (error) {
    return (
      <div className={`bg-red-50 border border-red-200 rounded-lg p-4 ${className}`}>
        <div className="flex items-center gap-2 text-red-700">
          <AlertTriangle className="w-5 h-5" />
          <span>{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      {/* Withdraw Consent Confirmation Dialog */}
      {showWithdrawDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-ink">Withdraw Consent?</h3>
              <button
                onClick={() => setShowWithdrawDialog(false)}
                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                disabled={actionLoading}
              >
                <X className="w-5 h-5 text-muted" />
              </button>
            </div>
            <div className="p-4">
              <p className="text-ink leading-relaxed">
                If you withdraw your consent, you will no longer be able to use Hesvara to manage
                your caregiving information. Your data will be retained per our data retention
                policy but you won't have access to it.
              </p>
              <p className="text-sm text-muted mt-3">
                You will be signed out after withdrawing consent.
              </p>
            </div>
            <div className="flex justify-end gap-3 p-4 border-t border-gray-200 bg-gray-50 rounded-b-lg">
              <button
                onClick={() => setShowWithdrawDialog(false)}
                className="px-4 py-2 text-sm font-medium text-ink bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                disabled={actionLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleWithdrawConsent}
                disabled={actionLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {actionLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                Withdraw Consent
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Action Error Display */}
      {actionError && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <span className="text-sm">{actionError}</span>
          <button
            onClick={() => setActionError(null)}
            className="ml-auto p-1 hover:bg-red-100 rounded-full"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Current Consent Status */}
      <div className="mb-4">
        <h4 className="font-semibold text-ink mb-3">Platform Consent Status</h4>

        {currentConsent ? (
          <div className="flex items-start gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="w-6 h-6 bg-evergreen rounded-full flex items-center justify-center flex-shrink-0">
              <Check className="w-3.5 h-3.5 text-white" />
            </div>
            <div className="flex-1">
              <p className="font-medium text-green-800">Consent Active</p>
              <p className="text-sm text-green-700 mt-1">
                Version {currentConsent.version} • Granted on {formatDate(currentConsent.grantedAt)}
              </p>
            </div>
            <button
              onClick={() => setShowWithdrawDialog(true)}
              className="px-3 py-1.5 text-sm font-medium text-red-600 bg-white border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
            >
              Withdraw Consent
            </button>
          </div>
        ) : hasAnyPlatformConsent ? (
          <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center flex-shrink-0">
              <Clock className="w-3.5 h-3.5 text-white" />
            </div>
            <div className="flex-1">
              <p className="font-medium text-amber-800">Consent Update Available</p>
              <p className="text-sm text-amber-700 mt-1">
                A new version of our consent policy is available. Your previous consent remains valid.
              </p>
            </div>
            <button
              onClick={() => setShowWithdrawDialog(true)}
              className="px-3 py-1.5 text-sm font-medium text-red-600 bg-white border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
            >
              Withdraw Consent
            </button>
          </div>
        ) : (
          <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-3.5 h-3.5 text-white" />
            </div>
            <div className="flex-1">
              <p className="font-medium text-red-800">No Consent on Record</p>
              <p className="text-sm text-red-700 mt-1">
                You must provide consent to use Hesvara's caregiving services.
              </p>
            </div>
            <button
              onClick={handleProvideConsent}
              disabled={actionLoading}
              className="px-3 py-1.5 text-sm font-medium text-white bg-evergreen rounded-lg hover:bg-deep-forest transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {actionLoading && <Loader2 className="w-4 h-4 animate-spin" />}
              Provide Consent
            </button>
          </div>
        )}
      </div>

      {/* Current Consent Text */}
      <div className="mb-4">
        <h4 className="font-semibold text-ink mb-2">Current Consent Terms</h4>
        <div className="bg-airy rounded-lg p-4">
          <p className="text-sm text-ink leading-relaxed">{PLATFORM_CONSENT_TEXT}</p>
          <p className="text-xs text-muted mt-2">Version {CURRENT_CONSENT_VERSION}</p>
        </div>
      </div>

      {/* Consent History Toggle */}
      {consents.length > 0 && (
        <div>
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="flex items-center gap-2 text-sm font-medium text-evergreen hover:text-deep-forest transition-colors"
          >
            {showHistory ? (
              <>
                <ChevronUp className="w-4 h-4" />
                Hide Consent History
              </>
            ) : (
              <>
                <ChevronDown className="w-4 h-4" />
                View Consent History ({consents.length} record{consents.length !== 1 ? 's' : ''})
              </>
            )}
          </button>

          {showHistory && (
            <div className="mt-3 space-y-2">
              {consents.map((consent) => (
                <div
                  key={consent.consentId}
                  className="flex items-center justify-between p-3 bg-soft-neutral rounded-lg text-sm"
                >
                  <div className="flex items-center gap-2">
                    {consent.granted ? (
                      <Check className="w-4 h-4 text-evergreen" />
                    ) : (
                      <AlertTriangle className="w-4 h-4 text-red-500" />
                    )}
                    <span className="capitalize">
                      {consent.consentType.replace('_', ' ')}
                    </span>
                    <span className="text-muted">v{consent.version}</span>
                  </div>
                  <div className="text-muted">
                    {consent.granted
                      ? `Granted ${formatDate(consent.grantedAt)}`
                      : `Withdrawn ${formatDate(consent.withdrawnAt)}`}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ConsentManager;
